import { parseAuthCallback } from '@nocturn/shared';

/**
 * What the URL said when this page was loaded, read before anything eats it.
 *
 * Imported by main.tsx ahead of everything else, because the answer survives
 * for only a moment: `detectSessionInUrl` consumes the fragment and rewrites
 * the address bar, so a component mounting afterwards sees a perfectly
 * ordinary /account URL with no trace of how the visitor got there.
 *
 * Two questions are asked of it, and both were previously unanswerable.
 */

const href = typeof window === 'undefined' ? '' : window.location.href;

/**
 * Did this page load begin with a password-reset link?
 *
 * The auth event is the other half of the answer — the two are belt and
 * braces. Which one arrives depends on whether the project is on the implicit
 * flow (tokens and `type=recovery` in the fragment) or PKCE (a bare `code`,
 * where only the event tells recovery apart from an ordinary sign-in), and
 * that is a dashboard setting this code does not control.
 */
function askedForRecovery(): boolean {
  if (!href) return false;

  const [beforeHash, hash = ''] = href.split('#');
  const query = beforeHash.slice(beforeHash.indexOf('?') + 1);

  return (
    new URLSearchParams(hash).get('type') === 'recovery' ||
    new URLSearchParams(query).get('type') === 'recovery'
  );
}

export const arrivedForRecovery = askedForRecovery();

/**
 * Did the link refuse to sign anyone in?
 *
 * Supabase reports a rejected link by bouncing back to the site with the
 * reason in the URL and no session — an expired magic link, a reset used
 * twice, or, the one that matters on cutover day, a redirect URL missing from
 * the project's allow-list. Nothing read it, so all three arrived as the
 * sign-in form redrawn exactly as it was, which reads as the button not
 * working. CUTOVER.md calls the allow-list "the step most likely to be
 * forgotten, because everything looks fine without it"; this is what stops it
 * looking fine.
 */
function landingError(): string | null {
  const callback = parseAuthCallback(href);
  return callback?.kind === 'error' ? callback.message : null;
}

export const arrivedWithError = landingError();
